/**
 * NMU-602: give a typed-in link the https:// an editor left off.
 *
 * Editors type addresses the way people say them — `egp.nmu.ac.th` — and the
 * site used that as-is, where a browser reads it as a path on the site itself:
 * the portal opened its own 404. nmu-web now repairs such links when it draws
 * them (src/lib/links.ts, same rules as here); this makes the form save the
 * address it means in the first place, and shows the editor the https:// on
 * leaving the field, so what is stored is what they saw.
 *
 * Left exactly as typed: http:// and https:// addresses, mailto: and tel:, and
 * this site's own paths (/…, #…, ?…, and //host). Any other scheme —
 * javascript:, data: — is not an address; withProtocol leaves it alone and
 * isUnsafeLink says so, and the form refuses it.
 */

const ALLOWED_SCHEME = /^(https?:\/\/|mailto:|tel:)/i;
const ANY_SCHEME = /^([a-z][a-z0-9+.-]*):/i;
/** `host:port…` — looks like `scheme:` to a naive check, but is an address. */
const HOST_PORT = /^[^/:?#]+:\d+(?:[/?#]|$)/;

const isSitePath = (value) => value.startsWith("/") || value.startsWith("#") || value.startsWith("?");

/** True for a scheme that is not an address (javascript:, data:, …). */
export const isUnsafeLink = (raw) => {
  const value = String(raw == null ? "" : raw).trim();
  if (!value || isSitePath(value) || ALLOWED_SCHEME.test(value)) return false;
  if (/^https?:/i.test(value)) return false; // a mistyped http(s) scheme — repaired below
  return ANY_SCHEME.test(value) && !HOST_PORT.test(value);
};

/**
 * The address to save for what was typed. Blank stays blank; an unsafe scheme
 * is returned unchanged (the validator rejects it rather than this guessing).
 */
export const withProtocol = (raw) => {
  const value = String(raw == null ? "" : raw).trim();
  if (!value || isSitePath(value) || ALLOWED_SCHEME.test(value)) return value;

  // `http:egp…` / `https:/egp…` — the scheme is there, its slashes are not.
  const typo = value.match(/^(https?):\/*(.+)$/i);
  if (typo) return `${typo[1].toLowerCase()}://${typo[2]}`;

  if (isUnsafeLink(value)) return value;
  return `https://${value}`;
};
